/**
 *  BÉR – ÁLLÓ A4-ES DOKUMENTUMOK NYOMTATÁSA (igazolások, bérjegyzék).
 *
 *  Ugyanaz a megközelítés, mint a Könyvelés rács-nyomtatásánál (naplo/naplo-nyomtatas.ts): nem a
 *  képernyőt nyomtatjuk, hanem egy rejtett keretbe (iframe) tiszta HTML-dokumentumot írunk és azt.
 *  Itt álló lap, és TÖBB dokumentum egy nyomtatásban (egy dolgozó = egy lap, laptöréssel) – így
 *  egy hónap összes bérjegyzéke vagy egy év összes igazolása egyszerre kinyomtatható.
 *
 *  A tartalmat a hívó adja (lap-HTML-ek); a közös elemek (fejléc, munkáltató/munkavállaló doboz,
 *  adatsor-tábla, aláírás, „nem hivatalos" jelzés) segédfüggvényei itt vannak.
 *
 *  Az asztali programban PDF-előnézet nyílik (a Windows nyomtatóablakának nincs előnézete) – nyomtatas-elonezet.ts.
 */

import { htmlElonezet } from '../../nyomtatas-elonezet';

export const esc = (v: any) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export const ft = (v: any): string => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n).toLocaleString('hu-HU') : '';
};

export const datum = (v: any): string => {
  const s = String(v ?? '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s.replace(/-/g, '. ') + '.' : (s || '—');
};

/** Kétoszlopos fél-doboz (munkáltató / munkavállaló). */
export function felDoboz(cim: string, sorok: [string, any][]): string {
  return `<div class="doboz"><div class="doboz-cim">${esc(cim)}</div>${sorok
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `<div class="sor"><span>${esc(k)}</span><b>${esc(v)}</b></div>`).join('')}</div>`;
}

/** Címke–érték tábla (sorszám, megnevezés, összeg). */
export function adatTabla(fejlec: string[], sorok: string[][], szamOszlopok: number[] = [], osszesen?: string[]): string {
  const th = fejlec.map((f, i) => `<th class="${szamOszlopok.includes(i) ? 'num' : ''}">${esc(f)}</th>`).join('');
  const tr = sorok.map((r) => `<tr>${r.map((c, i) => `<td class="${szamOszlopok.includes(i) ? 'num' : ''}">${esc(c)}</td>`).join('')}</tr>`).join('');
  const ossz = osszesen ? `<tr class="osszesen">${osszesen.map((c, i) => `<td class="${szamOszlopok.includes(i) ? 'num' : ''}">${esc(c)}</td>`).join('')}</tr>` : '';
  return `<table class="racs"><thead><tr>${th}</tr></thead><tbody>${tr || `<tr><td colspan="${fejlec.length}" class="ures">Nincs adat.</td></tr>`}${ossz}</tbody></table>`;
}

export function alairas(hely = ''): string {
  const ma = new Date().toLocaleDateString('hu-HU');
  return `<div class="alairas"><div>Kelt: ${esc(hely)}${hely ? ', ' : ''}${esc(ma)}</div>
    <div class="vonal"><span>munkáltató (cégszerű aláírás)</span></div></div>`;
}

export function nemHivatalos(szoveg: string): string {
  return `<div class="nem-hivatalos">${esc(szoveg)}</div>`;
}

function dokumentumHtml(cim: string, lapok: string[], lablec: string, fekvo = false): string {
  const ido = new Date().toLocaleString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  return `<!doctype html><html lang="hu"><head><meta charset="utf-8"><title>${esc(cim)}</title>
<style>
  @page { size: A4 ${fekvo ? 'landscape' : 'portrait'}; margin: ${fekvo ? '10mm 10mm 12mm' : '13mm 13mm 15mm'};
    @bottom-left { content: "${lablec.replace(/"/g, "'")} · nyomtatva: ${ido}"; font: 7pt Arial, sans-serif; color: #666; }
    @bottom-right { content: counter(page) ". / " counter(pages) ". oldal"; font: 7pt Arial, sans-serif; color: #666; } }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { margin: 0; font: 9pt/1.35 Arial, "Helvetica Neue", sans-serif; color: #111; }
  /*  Semmi ne lógjon túl a nyomtatható szélességen: a táblázat legfeljebb a lap széles, a hosszú szöveg tördelődik,
   *  és az összevont keretvonal (border-collapse) külső fele se kerüljön a margóra – azt a Firefox levágja. */
  html, body { width: 100%; max-width: 100%; }
  .lap { max-width: 100%; overflow-wrap: break-word; }
  table { max-width: 100%; }
  table[class] { width: calc(100% - 1.5pt) !important; margin-left: .75pt !important; margin-right: .75pt !important; }
  .lap { break-after: page; page-break-after: always; }
  .lap:last-child { break-after: auto; page-break-after: auto; }
  h1 { font-size: 13.5pt; margin: 0 0 2pt; letter-spacing: .01em; }
  .alcim { font-size: 8.5pt; color: #333; margin-bottom: 8pt; }
  .fej { border-bottom: 1.5pt solid #111; padding-bottom: 5pt; margin-bottom: 8pt; display: flex; justify-content: space-between; align-items: flex-end; gap: 12pt; }
  .fej .jobb { text-align: right; font-size: 8.5pt; color: #333; }
  .felek { display: grid; grid-template-columns: 1fr 1fr; gap: 8pt; margin-bottom: 8pt; }
  .doboz { border: .8pt solid #999; padding: 5pt 7pt; break-inside: avoid; }
  .doboz-cim { font-size: 7.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #444; margin-bottom: 3pt; }
  .doboz .sor { display: flex; justify-content: space-between; gap: 8pt; font-size: 8.5pt; padding: 1pt 0; }
  .doboz .sor span { color: #444; } .doboz .sor b { text-align: right; }
  h2 { font-size: 9.5pt; margin: 10pt 0 4pt; text-transform: uppercase; letter-spacing: .04em; border-bottom: .8pt solid #111; padding-bottom: 2pt; }
  table.racs { width: 100%; border-collapse: collapse; margin-bottom: 4pt; }
  table.racs th { background: #e9ecef; text-align: left; font-size: 7.8pt; padding: 3pt 4pt; border-top: .8pt solid #111; border-bottom: .8pt solid #111; }
  table.racs td { padding: 2.4pt 4pt; border-bottom: .4pt solid #cfd4d9; font-size: 8.5pt; vertical-align: top; }
  table.racs tr { break-inside: avoid; }
  table.racs .num { text-align: right; font-variant-numeric: tabular-nums; }
  table.racs td.num { white-space: nowrap; }   /* a fejléc tördelődhet – a sok oszlopos tábla (Jelenlétek) így fér a lapra */
  table.racs tr.osszesen td { font-weight: 700; border-top: 1pt solid #111; background: #f3f4f6; }
  table.racs td.ures { text-align: center; color: #666; padding: 8pt; }
  .kiemelt { display: flex; justify-content: space-between; font-size: 11pt; font-weight: 700; border: 1.2pt solid #111; padding: 5pt 8pt; margin: 8pt 0; }
  .megj { font-size: 7.8pt; color: #333; margin: 4pt 0; }
  .megj li { margin: 1pt 0; }
  .nem-hivatalos { border: .8pt dashed #777; color: #444; font-size: 7.6pt; padding: 4pt 6pt; margin: 0 0 8pt; }
  .alairas { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 24pt; font-size: 8.5pt; }
  .alairas .vonal { width: 62mm; border-top: .8pt solid #111; text-align: center; padding-top: 2pt; font-size: 7.5pt; color: #444; }
  .atvetel { margin-top: 14pt; font-size: 8pt; display: flex; justify-content: flex-end; }
  .atvetel .vonal { width: 62mm; border-top: .8pt solid #111; text-align: center; padding-top: 2pt; font-size: 7.5pt; color: #444; margin-top: 18pt; }
  /* Átvételi lista: magas sorok az aláírásnak; a fejléc minden lapon megismétlődik. */
  table.racs thead { display: table-header-group; }
  table.racs.atveteli td { height: 11mm; vertical-align: middle; }
  table.racs.atveteli td.alairo { width: 42mm; border-bottom: .8pt solid #111; }
  table.racs td.kicsi, .kicsi { font-size: 7.2pt; color: #444; }
  table.racs tr.csoport td { font-weight: 700; background: #f3f4f6; }
  /* Bérjegyzék (berjegyzek.ts): két hasáb tömör címke–érték blokkokkal, hogy egy A4-re férjen. */
  .bj-felek { display: flex; justify-content: space-between; gap: 12pt; font-size: 8.5pt; margin-bottom: 6pt; }
  .bj-felek .jobb { text-align: right; }
  .bj-racs { display: grid; grid-template-columns: 1fr 1fr; gap: 0 12pt; }
  .bj-hasab { min-width: 0; }
  .bj-blokk { break-inside: avoid; margin-bottom: 6pt; }
  .bj-blokk h3 { font-size: 8pt; margin: 0 0 2pt; text-transform: uppercase; letter-spacing: .04em; border-bottom: .8pt solid #111; padding-bottom: 1.5pt; }
  table.bj-kv { width: 100%; border-collapse: collapse; }
  table.bj-kv td { font-size: 8pt; padding: 1.2pt 2pt; border-bottom: .3pt solid #dde1e5; vertical-align: top; }
  table.bj-kv td.bj-x { color: #555; font-size: 7.2pt; text-align: right; white-space: nowrap; }
  table.bj-kv td.num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; width: 1%; padding-left: 6pt; }
  table.bj-kv tr.bj-ossz td { font-weight: 700; border-top: .8pt solid #111; }
  table.bj-naptar { width: 100%; border-collapse: collapse; table-layout: fixed; }
  table.bj-naptar th { font-size: 6.8pt; color: #555; font-weight: 600; padding: 1pt; }
  table.bj-naptar td { border: .4pt solid #c5cad0; text-align: center; padding: 1pt 0; font-size: 7.6pt; height: 15pt; }
  table.bj-naptar td.pihen { background: #eef0f2; }
  table.bj-naptar td.ures { border: 0; }
  table.bj-naptar .nap { display: block; font-size: 5.8pt; color: #666; line-height: 1; }
  .bj-jm { display: flex; flex-wrap: wrap; gap: 1pt 8pt; font-size: 6.8pt; color: #444; margin-top: 2pt; }
  .bj-vegso { border-top: 1.2pt solid #111; padding-top: 4pt; margin-top: 2pt; display: grid; grid-template-columns: 1fr 1fr; gap: 0 12pt; align-items: end; }
  .bj-vegso .kiemelt { margin: 0; }
</style></head><body>${lapok.map((l) => `<section class="lap">${l}</section>`).join('')}</body></html>`;
}

/** Nyomtatás rejtett keretből: a képernyő nem változik, a böngésző nyomtatási ablaka nyílik
 *  (asztali programban előbb a PDF-előnézet). `fekvo`: fekvő A4 (a sok oszlopos listákhoz). */
export function dokumentumNyomtat(cim: string, lapok: string[], lablec = 'Cronomit · Bérszámfejtés', fekvo = false): void {
  const html = dokumentumHtml(cim, lapok, lablec, fekvo);
  htmlElonezet(html, cim, () => keretNyomtat(html, fekvo));
}

function keretNyomtat(html: string, fekvo: boolean): void {
  const keret = document.createElement('iframe');
  keret.setAttribute('aria-hidden', 'true');
  //  A keret szélessége a lap NYOMTATHATÓ szélessége (A4 210 mm − 2 × 13 mm margó): ha a böngésző a keret
  //  szélességével tördel (Firefox), a teljes A4-szélesség (794 px) miatt a táblázatok jobb széle lelógott.
  keret.style.cssText = `position:fixed;left:-10000px;top:0;width:${fekvo ? '277mm' : '184mm'};height:${fekvo ? '188mm' : '267mm'};border:0;visibility:hidden`;
  document.body.appendChild(keret);
  const doc = keret.contentDocument!;
  doc.open();
  doc.write(html);
  doc.close();
  const takarit = () => setTimeout(() => keret.remove(), 1000);
  keret.contentWindow!.addEventListener('afterprint', takarit);
  setTimeout(() => {
    try { keret.contentWindow!.focus(); keret.contentWindow!.print(); } finally { setTimeout(takarit, 60000); }
  }, 250);
}
